import { Stack } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { ChatRailTokens } from "../../tokens";
import { JsonResultRenderer } from "./JsonResultRenderer";
import { ScalarRow, pickStringField } from "./registry";

function formatAmount(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  const amount = o.amount ?? o.deal_amount ?? o.value;
  const currency = (o.currency as string) ?? (o.currency_code as string) ?? "USD";
  if (typeof amount !== "number") return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

export function DealRenderer({
  kind,
  payload,
  T,
}: {
  kind: "invocation" | "result";
  payload: unknown;
  T: ChatRailTokens;
}) {
  if (kind === "invocation") return <JsonResultRenderer payload={payload} T={T} />;

  const id = pickStringField(payload, "id", "deal_id");
  const name = pickStringField(payload, "name", "title", "deal_name");
  const stage = pickStringField(payload, "stage", "stage_name");
  const amount = formatAmount(payload);

  if (!name && !id) return <JsonResultRenderer payload={payload} T={T} />;

  return (
    <Stack spacing={0.5}>
      {name && id ? (
        <ScalarRow
          label="deal"
          value={
            <RouterLink to={`/deals/${id}`} style={{ color: T.accent, textDecoration: "none" }}>
              {name}
            </RouterLink>
          }
          T={T}
        />
      ) : name ? (
        <ScalarRow label="deal" value={name} T={T} />
      ) : null}
      {stage && <ScalarRow label="stage" value={stage} T={T} />}
      {amount && <ScalarRow label="amount" value={amount} T={T} />}
    </Stack>
  );
}
